import { sentryLogger } from "@robe/core";
import type { Redis as RedisType, ValueType } from "ioredis";

let redis: RedisType | null = null;

const ensureRedis = async () => {
  if (!redis) {
    const { default: Redis } = await import("ioredis");

    redis = new Redis({
      host: process.env.REDIS_HOST ?? "127.0.0.1",
      port: process.env.REDIS_PORT ? +process.env.REDIS_PORT : 6379,
      db: process.env.REDIS_DB ? +process.env.REDIS_DB : 2,
      password: process.env.REDIS_PASSWD,
      ...(process.env.REDIS_TLS === "true" ? { tls: {} } : {}),
    });

    redis.on("error", (error) => {
      if (typeof error === "object" && "code" in error) {
        console.error("\x1b[31mRedis connection error:\x1b[0m", error.code);
      } else {
        console.error("\x1b[31mRedis connection error:\x1b[0m", error);
      }

      sentryLogger({
        error,
        message: "An unknown redis connection error occurred",
      });
    });
  }
};

export const createCache = <TResult extends any, TArgs extends any[]>(
  get: (...args: TArgs) => Promise<TResult>,
  getKey: (...args: TArgs) => string,
  {
    serialize = (r) => Buffer.from(JSON.stringify(r), "utf8"),
    deserialize = (b) => JSON.parse(b.toString("utf8")),
    staleAge = 1200000, // Max age for determining staleness in milliseconds. Defaults to 20 minutes
  }: {
    serialize?: (r: TResult) => Buffer | null;
    deserialize?: (s: Buffer) => TResult;
    staleAge?: number;
  } = {}
): ((...args: TArgs) => Promise<TResult>) => {
  if (typeof window === "undefined") {
    return async (...args: TArgs) => {
      const bypassCache =
        process.env.BYPASS_REDIS_CACHE && process.env.BYPASS_REDIS_CACHE !== "" ? process.env.BYPASS_REDIS_CACHE === "true" : false;

      if (bypassCache) {
        return get(...args);
      }

      await ensureRedis();
      if (!redis) {
        console.warn("\x1b[33mRedis unavailable, switching to direct fetch\x1b[0m");

        return get(...args);
      }

      const isDevEnv = process.env.XCORE_ENV === "dev";

      isDevEnv && console.info("redis available");

      const key = getKey(...args);

      try {
        const [cached, stat] = await Promise.all([redis.getBuffer(key), redis.get(`_keys:${key}`)]);

        if (!cached) {
          isDevEnv && console.info("key not found in redis cached data");

          const fresh = await get(...args);
          await setValue(key)(serialize(fresh));
          return fresh;
        }

        isDevEnv && console.info("key found in redis cached data");

        const age = Math.abs(+stat! - Date.now());

        if (age > staleAge) {
          isDevEnv && console.info("redis cached data staleness expired");

          get(...args)
            .then(serialize)
            .then(setValue(key))
            .catch(() => null);
        }

        return deserialize(cached);
      } catch (error) {
        sentryLogger({
          error,
          message: "Unexpected error occurred while trying to access redis, switching to direct fetch",
          context: {
            key,
            args,
          },
        });

        console.warn("\x1b[31mUnexpected error occurred while trying to access redis, switching to direct fetch:\x1b[0m", error);

        return get(...args);
      }
    };
  }

  return get;
};

const setValue = (key: string) => async (value: ValueType | null) => {
  if (process.env.NODE_ENV !== "production" || process.env.VERBOSE === "1") {
    console.info(`\x1b[33mredis\x1b[0m - updating value for ${key}`);
  }

  if (redis) {
    const ttl = Number(process.env.REDIS_TTL_SECONDS ?? 3600);

    if (key && value) {
      // Set value with TTL
      await redis.set(key, value, "EX", ttl);
    }

    // Set metadata key with TTL
    await redis.set(`_keys:${key}`, Date.now(), "EX", ttl);
  }
};
